import React, { useState, useEffect } from 'react';
import { Box, Typography, Fade, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { keyframes } from '@mui/system';

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

interface ScrollIndicatorProps {
  onClick?: () => void;
  text?: string;
}

const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({ 
  onClick, 
  text = 'Scroll to learn more' 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const handleScroll = () => {
      // Hide indicator after scrolling down 50px
      if (window.scrollY > 50) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default scroll behavior
      window.scrollTo({
        top: window.innerHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Fade in={isVisible} timeout={500}>
      <Box
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', md: 12 },
          left: { xs: 'auto', md: 0 },
          right: { xs: 12, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          animation: `${bounce} 2s infinite`,
          zIndex: 100,
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        {!isMobile && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.82rem', md: '1rem' },
              mb: 0.5,
            }}
          >
            {text}
          </Typography>
        )}
        <Box
          aria-label="Scroll"
          sx={{
            width: { xs: 36, md: 'auto' },
            height: { xs: 36, md: 'auto' },
            borderRadius: { xs: '50%', md: 0 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: { xs: 'rgba(255,255,255,0.08)', md: 'transparent' },
            border: { xs: '1px solid rgba(255,255,255,0.18)', md: 'none' },
            backdropFilter: { xs: 'blur(6px)', md: 'none' },
          }}
        >
          <KeyboardArrowDown
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: { xs: 22, md: 28 },
              mx: { md: 'auto' },
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default ScrollIndicator;
